import bridge from '@/plugins/vue-constructor-bridge';

import http from '@/services/http';
import moment from 'moment-timezone';
import events from '@/services/events';
import errorHandlers from '@/services/error-handlers';
import normalizeSavedCardRequestBody from '@/helpers/normalize-saved-cards';

export default {
  namespaced: true,

  state: {
    areLoading: false,
    deletingCardId: null,
    cards: [],
    hasLoaded: false,
    isCreating: false
  },

  getters: {
    creditCardManagementEnabled(state, getters, rootState, rootGetters) {
      const merchant = rootGetters['merchant/merchant'];
      return !merchant.features['disable-saved-payment-management'] && !!merchant.defaultPaymentGateway.methods.savedCreditCard;
    },

    savedCards(state) {
      const cards = state.cards.map((card) => {
        const today = moment();
        const expiration = moment().year(card.expiration.year).month(card.expiration.month - 1);
        const expirationMonth = (`0${card.expiration.month.toString()}`).slice(-2);
        const expirationYear = card.expiration.year.toString().slice(-2);
        const isExpiringSoon = today.format('MM/YYYY') === expiration.format('MM/YYYY');
        const isExpired = expiration.diff(today) < 0;
        const expirationDisplay = `${expirationMonth}/${expirationYear}`;

        return {
          ...card,
          expirationDisplay,
          isExpired,
          isExpiringSoon
        };
      });

      const validCards = cards.filter(card => !card.isExpired);

      return {
        ...state,
        cards,
        validCards
      };
    }
  },

  actions: {
    async createCard({ commit, rootGetters }, { cardInformation, isSilentNotifiction }) {
      try {
        const currentUser = rootGetters['session/currentUser'];
        const merchant = rootGetters['merchant/merchant'];
        const body = await normalizeSavedCardRequestBody(cardInformation, currentUser, merchant);

        commit('SET_CREATE_LOADING_STATUS', true);

        const { data } = await http.post('/cardfree-api/v1/account/me/credit-card', body);

        commit('ADD_SAVED_CARD', data);

        // Vue.triggerGoogleTrackingEvent('SavePaymentMethod', 'Account');

        bridge.triggerNewRelicEvent('SavePaymentMethod', 'Information', 'Payment Method Saved');

        if (!isSilentNotifiction) {
          events.emit('notification', {
            type: 'success',
            message: 'Card information was saved.'
          });
        }

        return data;
      }

      catch (error) {
        if (isSilentNotifiction) {
          errorHandlers.silent(error);
        }

        else {
          errorHandlers.generic(error);
        }
      }

      finally {
        commit('SET_CREATE_LOADING_STATUS', false);
      }
    },

    async deleteCard({ commit, state }, cardId) {
      try {
        commit('SET_DELETE_LOADING_STATUS', cardId);

        await http.delete(`/cardfree-api/v1/account/me/credit-card/${cardId}`);

        const cards = state.cards.filter(card => card.creditCardId !== cardId);

        commit('SET_SAVED_CARDS', cards);

        events.emit('notification', {
          type: 'success',
          message: 'Card information was deleted.'
        });
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        commit('SET_DELETE_LOADING_STATUS', null);
      }
    },

    async getCards({ commit }) {
      try {
        commit('SET_LOADING_STATUS', true);

        const response = await http.get('/cardfree-api/v1/account/me/credit-card');

        commit('SET_SAVED_CARDS', response.data);
      }

      catch (error) {
        errorHandlers.generic(error);
      }

      finally {
        commit('SET_LOADING_STATUS', false);
      }
    }
  },

  mutations: {
    ADD_SAVED_CARD(state, card) {
      state.cards.forEach((c) => {
        c.isUsersDefaultCard = false;
      });

      state.cards.unshift(card);
    },

    SET_CREATE_LOADING_STATUS(state, isCreating) {
      state.isCreating = isCreating;
    },

    SET_DELETE_LOADING_STATUS(state, id) {
      state.deletingCardId = id;
    },

    SET_LOADING_STATUS(state, areLoading) {
      state.areLoading = areLoading;
    },

    SET_SAVED_CARDS(state, cards) {
      state.cards = cards || [];
      state.hasLoaded = true;
    }
  }
};
